// GLSL sources for the Stardust renderer
export const MAX_LIGHTS = 32;

const common = /* glsl */ `
float hash12(vec2 p){ vec3 p3 = fract(vec3(p.xyx) * .1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
vec3 dirToEquirectUV(vec3 d){ return vec3(atan(d.x, -d.z) / 6.2831853 + 0.5, acos(clamp(d.y,-1.,1.)) / 3.14159265, 0.); }
vec3 aces(vec3 x){ const float a=2.51,b=0.03,c=2.43,d=0.59,e=0.14; return clamp((x*(a*x+b))/(x*(c*x+d)+e),0.,1.); }
`;

export const standardVS = /* glsl */ `#version 300 es
layout(location=0) in vec3 aPos;
layout(location=1) in vec3 aNor;
layout(location=2) in vec3 aCol;
layout(location=3) in vec4 aMat;
uniform mat4 uModel;
uniform mat4 uViewProj;
uniform mat4 uShadowMat;
out vec3 vPos; out vec3 vNor; out vec3 vCol; out vec4 vMat; out vec4 vShadow; out vec3 vLocal;
void main(){
  vec4 wp = uModel * vec4(aPos,1.0);
  vPos = wp.xyz;
  vNor = mat3(uModel) * aNor;
  vCol = aCol; vMat = aMat; vLocal = aPos;
  vShadow = uShadowMat * wp;
  gl_Position = uViewProj * wp;
}`;

export const standardFS = /* glsl */ `#version 300 es
precision highp float;
precision highp sampler2DShadow;
#define MAXL ${MAX_LIGHTS}
in vec3 vPos; in vec3 vNor; in vec3 vCol; in vec4 vMat; in vec4 vShadow; in vec3 vLocal;
uniform vec3 uCamPos;
uniform vec4 uTint;
uniform vec3 uEmissive;
uniform float uEmitMul;
uniform vec3 uSunDir; uniform vec3 uSunColor;
uniform vec3 uSkyAmb; uniform vec3 uGroundAmb;
uniform vec3 uRimColor;
uniform int uNumLights;
uniform vec4 uLPos[MAXL];
uniform vec4 uLCol[MAXL];
uniform vec4 uLDir[MAXL];
uniform sampler2DShadow uShadowMap; uniform float uShadowOn; uniform float uShadowTexel;
uniform sampler2D uEnvMap; uniform float uEnvIntensity; uniform float uEnvLod;
uniform sampler2D uReflTex; uniform float uReflOn; uniform vec2 uViewport;
uniform vec3 uFogColor; uniform float uFogDensity; uniform float uFogHeight;
uniform float uClipY;
uniform float uLdr;
uniform float uTime;
uniform vec4 uBeam; // transporter effect: x = amount (1 = not yet materialised), yzw = shimmer colour
out vec4 outColor;
${common}
float D_GGX(float NoH, float a){ float a2=a*a; float f=(NoH*a2-NoH)*NoH+1.0; return a2/(3.14159*f*f+1e-5); }
float V_Smith(float NoV, float NoL, float a){ float k=a*.5; return 0.25/((NoV*(1.-k)+k)*(NoL*(1.-k)+k)+1e-5); }
vec3 F_Schlick(float VoH, vec3 f0){ return f0 + (1.-f0)*pow(1.-VoH,5.); }

vec3 envSample(vec3 d, float lod){
  vec2 uv = dirToEquirectUV(d).xy;
  return textureLod(uEnvMap, uv, lod).rgb;
}
float shadowAt(vec3 n, vec3 l){
  if(uShadowOn < .5) return 1.0;
  vec3 sp = vShadow.xyz / vShadow.w * .5 + .5;
  if(sp.x<0.||sp.x>1.||sp.y<0.||sp.y>1.||sp.z>1.) return 1.0;
  float bias = 0.0009 + 0.0022*(1.0-max(dot(n,l),0.));
  float s = 0.0;
  float t = uShadowTexel*1.35;
  // rotated poisson disc
  float ang = hash12(gl_FragCoord.xy)*6.2831;
  mat2 R = mat2(cos(ang),-sin(ang),sin(ang),cos(ang));
  vec2 P[8] = vec2[8](vec2(-.94,-.40),vec2(.95,-.77),vec2(-.09,-.93),vec2(.34,.29),vec2(-.92,.46),vec2(-.24,.82),vec2(.54,-.13),vec2(.79,.72));
  for(int i=0;i<8;i++){ s += texture(uShadowMap, vec3(sp.xy + R*P[i]*t*1.6, sp.z - bias)); }
  return s/8.0;
}
void main(){
  if(vPos.y < uClipY) discard;
  float beamAmt = uBeam.x;
  if(beamAmt > 0.001){
    // sparkly dissolve: flickering 2px cells, rolling bands up the body
    float cellN = hash12(floor(gl_FragCoord.xy / 2.0) + floor(uTime * 22.0) * vec2(13.1, 7.7));
    float band = 0.5 + 0.5 * sin(vPos.y * 16.0 - uTime * 10.0);
    if(cellN < beamAmt * (0.7 + 0.3 * band)) discard;
  }
  vec3 albedo = vCol * uTint.rgb;
  float rough = clamp(vMat.x, 0.04, 1.0);
  float metal = vMat.y;
  float emit = vMat.z;
  float refl = vMat.w;
  vec3 N = normalize(vNor);
  if(!gl_FrontFacing) N = -N;
  vec3 V = normalize(uCamPos - vPos);
  float NoV = max(dot(N,V), 1e-4);
  vec3 f0 = mix(vec3(0.04), albedo, metal);
  vec3 diffC = albedo*(1.0-metal);
  float a = rough*rough;
  vec3 col = vec3(0.0);
  // sun / key light
  {
    vec3 L = normalize(-uSunDir);
    float NoL = max(dot(N,L),0.0);
    if(NoL>0.0){
      vec3 H = normalize(L+V);
      float NoH = max(dot(N,H),0.), VoH = max(dot(V,H),0.);
      vec3 F = F_Schlick(VoH,f0);
      vec3 spec = D_GGX(NoH,a)*V_Smith(NoV,NoL,a)*F;
      float sh = shadowAt(N,L);
      col += (diffC*(1.0-F)/3.14159 + spec) * uSunColor * NoL * sh;
    }
  }
  // punctual lights
  for(int i=0;i<MAXL;i++){
    if(i>=uNumLights) break;
    vec3 Lv = uLPos[i].xyz - vPos;
    float d2 = dot(Lv,Lv);
    float range = uLPos[i].w;
    if(d2 > range*range) continue;
    float d = sqrt(d2);
    vec3 L = Lv/d;
    float att = clamp(1.0 - pow(d/range,4.0),0.,1.); att = att*att/(d2+1.0);
    if(uLCol[i].w > -1.5){
      float cd = dot(-L, uLDir[i].xyz);
      att *= smoothstep(uLCol[i].w, uLCol[i].w + uLDir[i].w, cd);
    }
    float NoL = max(dot(N,L),0.0);
    // wrap lighting for softness
    float wrap = max((dot(N,L)+0.25)/1.25,0.0);
    vec3 H = normalize(L+V);
    float NoH = max(dot(N,H),0.), VoH = max(dot(V,H),0.);
    vec3 F = F_Schlick(VoH,f0);
    vec3 spec = D_GGX(NoH,a)*V_Smith(NoV,max(NoL,1e-3),a)*F*NoL;
    col += (diffC*(1.0-F)/3.14159*wrap + spec) * uLCol[i].rgb * att;
  }
  // image based ambient: hemisphere + environment
  float up = N.y*0.5+0.5;
  vec3 hemi = mix(uGroundAmb, uSkyAmb, up);
  vec3 envD = envSample(N, uEnvLod) * uEnvIntensity;
  vec3 R = reflect(-V,N);
  vec3 Fv = f0 + (max(vec3(1.0-rough),f0)-f0)*pow(1.0-NoV,5.0);
  vec3 envS = envSample(R, rough*uEnvLod) * uEnvIntensity;
  // cheap occlusion from height (objects near the floor are darker)
  float ao = mix(0.55, 1.0, smoothstep(0.0, 0.6, vPos.y + 0.05));
  col += diffC*(hemi + envD*0.35)*ao;
  vec3 specAmb = envS*0.6 + hemi*0.4;
  if(uReflOn > 0.5 && refl > 0.0){
    vec2 suv = gl_FragCoord.xy / uViewport;
    suv.x += N.x*0.02; suv.y += N.z*0.02;
    vec3 rc = vec3(0.0);
    float spread = rough*0.012 + 0.0015;
    for(int k=-2;k<=2;k++){ rc += texture(uReflTex, suv + vec2(0.0, float(k)*spread)).rgb; }
    rc/=5.0;
    specAmb = mix(specAmb, rc, refl);
  }
  col += specAmb*Fv*mix(0.7,1.0,ao);
  // stylised rim light
  float rim = pow(1.0-NoV, 3.0);
  col += uRimColor * rim * (0.35 + 0.65*up) * (1.0-metal*0.5);
  // emissive
  col += albedo * emit * uEmitMul + uEmissive;
  if(beamAmt > 0.001){
    float spark = step(0.9, hash12(floor(gl_FragCoord.xy / 1.5) + floor(uTime * 30.0) * vec2(3.7, 9.1)));
    col = mix(col, uBeam.yzw * 2.4, clamp(beamAmt * 1.4, 0.0, 1.0) * 0.85) + uBeam.yzw * spark * 5.0 * beamAmt;
  }
  // fog
  float dist = length(uCamPos - vPos);
  float fog = 1.0 - exp(-uFogDensity*dist*dist*0.01);
  fog *= clamp(1.0 - (vPos.y - uFogHeight)*0.05, 0.3, 1.0);
  col = mix(col, uFogColor, clamp(fog,0.,1.));
  if(uLdr > 0.5){ col = pow(aces(col*1.1), vec3(1.0/2.2)); }
  outColor = vec4(col, uTint.a);
}`;

export const depthVS = /* glsl */ `#version 300 es
layout(location=0) in vec3 aPos;
uniform mat4 uModel; uniform mat4 uViewProj;
void main(){ gl_Position = uViewProj * uModel * vec4(aPos,1.0); }`;
export const depthFS = /* glsl */ `#version 300 es
precision mediump float; out vec4 o; void main(){ o = vec4(1.0); }`;

// Additive / holographic effects
export const fxFS = /* glsl */ `#version 300 es
precision highp float;
in vec3 vPos; in vec3 vNor; in vec3 vCol; in vec4 vMat; in vec4 vShadow; in vec3 vLocal;
uniform vec3 uCamPos; uniform vec4 uTint; uniform float uFxMode; uniform float uTime; uniform float uFxParam;
uniform float uClipY;
out vec4 outColor;
void main(){
  if(vPos.y < uClipY) discard;
  vec3 N = normalize(vNor); vec3 V = normalize(uCamPos - vPos);
  float ndv = abs(dot(N,V));
  vec3 c = vCol*uTint.rgb;
  float a = uTint.a;
  if(uFxMode < 0.5){
    // plain
  } else if(uFxMode < 1.5){
    // fresnel glass
    a *= pow(1.0-ndv, 2.5)*0.9 + 0.06;
  } else if(uFxMode < 2.5){
    // beam: fades with local y (0 at top = bright, param = height), soft edges
    float h = clamp((vLocal.y + uFxParam*0.5)/uFxParam, 0.0, 1.0);
    a *= pow(ndv, 1.5) * (0.35 + 0.65*h) * (0.85 + 0.15*sin(uTime*6.0 + vLocal.y*8.0));
  } else {
    // hologram scanlines
    float s = 0.6 + 0.4*sin(vPos.y*60.0 - uTime*8.0);
    a *= s * (pow(1.0-ndv,1.5)*0.8+0.2);
  }
  outColor = vec4(c*a, 1.0);
}`;

// Fullscreen triangle
export const fsVS = /* glsl */ `#version 300 es
out vec2 vUv;
void main(){ vec2 p = vec2((gl_VertexID<<1)&2, gl_VertexID&2); vUv = p; gl_Position = vec4(p*2.0-1.0,0.0,1.0); }`;

// sky drawn at the far plane (depth 1) so pixels hidden behind the kitchen are skipped by the depth test
export const skyVS = /* glsl */ `#version 300 es
out vec2 vUv;
void main(){ vec2 p = vec2((gl_VertexID<<1)&2, gl_VertexID&2); vUv = p; gl_Position = vec4(p*2.0-1.0,1.0,1.0); }`;

export const skyFS = /* glsl */ `#version 300 es
precision highp float;
in vec2 vUv;
uniform mat4 uInvViewProj;
uniform vec3 uCamPos;
uniform float uTime;
uniform float uEquirect;
uniform vec3 uNeb1; uniform vec3 uNeb2; uniform vec3 uNeb3; uniform vec3 uSpaceCol;
uniform vec3 uStarDir; uniform vec3 uStarCol;
uniform vec3 uPlanetDir; uniform float uPlanetSize; uniform vec3 uPlanetCol; uniform vec3 uPlanetCol2; uniform float uRing;
uniform float uNebIntensity;
uniform float uSeed;
out vec4 outColor;
${common}
float hash13(vec3 p3){ p3 = fract(p3 * .1031); p3 += dot(p3, p3.zyx + 31.32); return fract((p3.x + p3.y) * p3.z); }
float noise(vec3 p){ vec3 i=floor(p); vec3 f=fract(p); f=f*f*(3.-2.*f);
  return mix(mix(mix(hash13(i),hash13(i+vec3(1,0,0)),f.x),mix(hash13(i+vec3(0,1,0)),hash13(i+vec3(1,1,0)),f.x),f.y),
             mix(mix(hash13(i+vec3(0,0,1)),hash13(i+vec3(1,0,1)),f.x),mix(hash13(i+vec3(0,1,1)),hash13(i+vec3(1,1,1)),f.x),f.y),f.z); }
uniform float uOctaves;
float fbm(vec3 p){ float v=0., a=.5; for(int i=0;i<5;i++){ if(float(i)>=uOctaves) break; v+=a*noise(p); p=p*2.03+vec3(1.7,9.2,3.1); a*=.5; } return v; }
vec3 stars(vec3 d){
  vec3 c = vec3(0.0);
  for(int l=0;l<3;l++){
    float sc = 90.0 + float(l)*130.0;
    vec3 p = d*sc;
    vec3 id = floor(p);
    float h = hash13(id + uSeed + float(l)*17.0);
    if(h > 0.965){
      vec3 f = fract(p) - 0.5;
      vec3 off = vec3(hash13(id+1.3),hash13(id+2.1),hash13(id+3.7))-0.5;
      float dd = length(f - off*0.6);
      float tw = 0.6 + 0.4*sin(uTime*(1.0+h*5.0) + h*80.0);
      float b = smoothstep(0.12, 0.0, dd) * (h-0.965)*28.0 * tw;
      vec3 sc2 = mix(vec3(0.7,0.8,1.0), vec3(1.0,0.85,0.7), hash13(id+5.0));
      c += sc2*b*(1.5 - float(l)*0.4);
    }
  }
  return c;
}
void main(){
  vec3 d;
  if(uEquirect > 0.5){
    float phi = (vUv.x - 0.5)*6.2831853; float th = vUv.y*3.14159265;
    d = vec3(sin(th)*sin(phi), cos(th), -sin(th)*cos(phi));
  } else {
    vec4 p = uInvViewProj * vec4(vUv*2.0-1.0, 1.0, 1.0);
    d = normalize(p.xyz/p.w - uCamPos);
  }
  vec3 col = uSpaceCol * (0.6 + 0.4*(d.y*0.5+0.5));
  // nebula
  vec3 q = d*2.2 + vec3(uSeed*3.1, 0.0, uSeed);
  float n1 = fbm(q + vec3(0.0, uTime*0.004, 0.0));
  float n2 = fbm(q*1.8 + n1*1.5 + vec3(5.2,1.3,2.8));
  float n3 = fbm(q*3.5 + n2*2.0);
  float band = exp(-pow(dot(d, normalize(vec3(0.3,0.25,-1.0)))*1.2, 2.0)*1.2);
  float neb = smoothstep(0.35, 0.95, n2) * (0.35 + 0.9*band);
  vec3 nc = mix(uNeb1, uNeb2, smoothstep(0.3,0.8,n1));
  nc = mix(nc, uNeb3, smoothstep(0.55,0.9,n3)*0.8);
  col += nc * neb * uNebIntensity;
  // dark dust lanes
  float dust = smoothstep(0.5, 0.8, fbm(q*2.6+vec3(7.0)));
  col *= 1.0 - dust*0.55*band;
  col += stars(d) * (1.0 - neb*0.5);
  // distant star (sun) with glare
  float sd = max(dot(d, normalize(uStarDir)), 0.0);
  col += uStarCol * (pow(sd, 1400.0)*40.0 + pow(sd, 60.0)*0.5 + pow(sd, 6.0)*0.06);
  // planet with ring
  if(uPlanetSize > 0.0){
    vec3 pd = normalize(uPlanetDir);
    float cs = dot(d, pd);
    float ang = acos(clamp(cs,-1.,1.));
    vec3 rn = normalize(vec3(0.25, 1.0, 0.35));
    vec3 pc = pd * 10.0;
    float Rp = 10.0*sin(uPlanetSize);
    float dn = dot(d, rn);
    float t = abs(dn) > 1e-4 ? dot(pc, rn)/dn : -1.0;
    vec3 hit = d*t - pc;
    float rr = length(hit)/Rp;
    vec3 ringCol = vec3(0.0); float ringA = 0.0;
    if(uRing > 0.5 && t > 0.0 && rr > 1.35 && rr < 2.4){
      float bands = 0.55 + 0.45*sin(rr*38.0) * sin(rr*11.0+1.0);
      ringA = smoothstep(1.35,1.45,rr)*smoothstep(2.4,2.2,rr)*bands*0.85;
      // ring lit by star, shadowed by planet roughly
      float lit = 0.35 + 0.65*max(dot(normalize(hit), normalize(uStarDir)) , 0.0);
      ringCol = mix(uPlanetCol2, vec3(1.0,0.95,0.88), 0.5) * lit * 0.9;
    }
    if(ang < uPlanetSize){
      float x = ang/uPlanetSize;
      vec3 tangent = normalize(d - pd*cs + vec3(1e-5));
      vec3 nrm = normalize(-pd*sqrt(max(1.0-x*x,0.0)) + tangent*x);
      float light = max(dot(nrm, normalize(uStarDir)), 0.0);
      float stripes = fbm(vec3(dot(d, rn)*30.0/uPlanetSize*0.1, dot(d,tangent)*2.0, uSeed));
      vec3 pcol = mix(uPlanetCol, uPlanetCol2, stripes);
      vec3 planet = pcol * (0.015 + light*1.3);
      planet += uNeb2*pow(x, 5.0)*0.7*(0.3+light);
      col = planet;
      if(t > 0.0 && t < 10.0) col = mix(col, ringCol, ringA);
    } else {
      col = mix(col, ringCol, ringA);
      col += uNeb2 * exp(-(ang-uPlanetSize)*50.0) * 0.5;
    }
  }
  outColor = vec4(col, 1.0);
}`;

export const particleVS = /* glsl */ `#version 300 es
layout(location=0) in vec2 aCorner;
layout(location=1) in vec4 iPosSize;
layout(location=2) in vec4 iColor;
layout(location=3) in vec2 iRotType;
uniform mat4 uView; uniform mat4 uProj;
out vec2 vUv; out vec4 vColor; out float vType;
void main(){
  float r = iRotType.x;
  vec2 c = mat2(cos(r),-sin(r),sin(r),cos(r)) * aCorner;
  vec4 vp = uView * vec4(iPosSize.xyz,1.0);
  vp.xy += c * iPosSize.w;
  gl_Position = uProj * vp;
  vUv = aCorner; vColor = iColor; vType = iRotType.y;
}`;
export const particleFS = /* glsl */ `#version 300 es
precision highp float;
in vec2 vUv; in vec4 vColor; in float vType;
uniform float uAdditive;
out vec4 o;
void main(){
  float d = length(vUv);
  float a;
  if(vType < 0.5){ a = pow(max(1.0-d,0.0), 2.0); }
  else if(vType < 1.5){ // sparkle
    float cr = max(0.0, 1.0 - abs(vUv.x)*8.0) * max(0.0,1.0-abs(vUv.y)) + max(0.0, 1.0 - abs(vUv.y)*8.0) * max(0.0,1.0-abs(vUv.x));
    a = pow(max(1.0-d,0.0),4.0) + cr*0.8;
  } else if(vType < 2.5){ // smoke puff
    a = smoothstep(1.0, 0.2, d) * 0.6;
  } else { // ring
    a = smoothstep(0.15, 0.0, abs(d-0.8));
  }
  a *= vColor.a;
  if(uAdditive > 0.5) o = vec4(vColor.rgb*a, 1.0);
  else o = vec4(vColor.rgb, a);
}`;

// ---- post processing ----
export const prefilterFS = /* glsl */ `#version 300 es
precision highp float; in vec2 vUv; uniform sampler2D uTex; uniform vec2 uTexel; uniform float uThreshold; out vec4 o;
vec3 s(vec2 uv){ return texture(uTex, uv).rgb; }
float lum(vec3 c){ return dot(c, vec3(0.2126,0.7152,0.0722)); }
void main(){
  vec3 a = s(vUv + uTexel*vec2(-1,-1)), b = s(vUv + uTexel*vec2(1,-1)), c = s(vUv + uTexel*vec2(-1,1)), d = s(vUv + uTexel*vec2(1,1));
  // karis average to kill fireflies
  float wa = 1.0/(1.0+lum(a)), wb = 1.0/(1.0+lum(b)), wc = 1.0/(1.0+lum(c)), wd = 1.0/(1.0+lum(d));
  vec3 col = (a*wa+b*wb+c*wc+d*wd)/(wa+wb+wc+wd);
  float l = lum(col);
  float knee = uThreshold*0.5;
  float soft = clamp(l - uThreshold + knee, 0.0, 2.0*knee);
  soft = soft*soft/(4.0*knee+1e-4);
  float contrib = max(soft, l - uThreshold)/max(l,1e-4);
  o = vec4(min(col*contrib, vec3(60.0)), 1.0);
}`;
export const downFS = /* glsl */ `#version 300 es
precision highp float; in vec2 vUv; uniform sampler2D uTex; uniform vec2 uTexel; out vec4 o;
void main(){
  vec3 c = texture(uTex, vUv).rgb*4.0;
  c += texture(uTex, vUv + uTexel*vec2(-1,-1)).rgb;
  c += texture(uTex, vUv + uTexel*vec2(1,-1)).rgb;
  c += texture(uTex, vUv + uTexel*vec2(-1,1)).rgb;
  c += texture(uTex, vUv + uTexel*vec2(1,1)).rgb;
  o = vec4(c/8.0,1.0);
}`;
export const upFS = /* glsl */ `#version 300 es
precision highp float; in vec2 vUv; uniform sampler2D uTex; uniform vec2 uTexel; uniform float uWeight; out vec4 o;
void main(){
  vec3 c = vec3(0.0);
  c += texture(uTex, vUv + uTexel*vec2(-1,-1)).rgb;
  c += texture(uTex, vUv + uTexel*vec2(0,-1)).rgb*2.0;
  c += texture(uTex, vUv + uTexel*vec2(1,-1)).rgb;
  c += texture(uTex, vUv + uTexel*vec2(-1,0)).rgb*2.0;
  c += texture(uTex, vUv).rgb*4.0;
  c += texture(uTex, vUv + uTexel*vec2(1,0)).rgb*2.0;
  c += texture(uTex, vUv + uTexel*vec2(-1,1)).rgb;
  c += texture(uTex, vUv + uTexel*vec2(0,1)).rgb*2.0;
  c += texture(uTex, vUv + uTexel*vec2(1,1)).rgb;
  o = vec4(c/16.0*uWeight,1.0);
}`;
export const compositeFS = /* glsl */ `#version 300 es
precision highp float; in vec2 vUv;
uniform sampler2D uHdr; uniform sampler2D uBloom;
uniform float uBloomStrength; uniform float uExposure; uniform float uTime;
uniform float uVignette; uniform float uGrain; uniform float uAberration; uniform float uSaturation;
uniform vec3 uLift; uniform vec3 uGain; uniform float uFlash; uniform vec3 uFlashColor;
uniform float uDesat;
out vec4 o;
${common}
void main(){
  vec2 cuv = vUv - 0.5;
  float r2 = dot(cuv,cuv);
  vec3 hdr;
  if(uAberration > 0.0){
    vec2 off = cuv * r2 * uAberration;
    hdr = vec3(texture(uHdr, vUv - off).r, texture(uHdr, vUv).g, texture(uHdr, vUv + off).b);
  } else hdr = texture(uHdr, vUv).rgb;
  vec3 bloom = texture(uBloom, vUv).rgb;
  vec3 c = hdr + bloom*uBloomStrength;
  c *= uExposure;
  c = aces(c);
  // grading
  c = pow(c, vec3(1.0/2.2));
  c = c*uGain + uLift*(1.0-c);
  float l = dot(c, vec3(0.299,0.587,0.114));
  c = mix(vec3(l), c, uSaturation*(1.0-uDesat));
  c *= 1.0 - uVignette*smoothstep(0.15, 0.85, r2*1.6);
  c += uFlashColor*uFlash;
  c += (hash12(vUv*1000.0 + fract(uTime)*100.0)-0.5)*uGrain;
  o = vec4(clamp(c,0.,1.),1.0);
}`;
export const fxaaFS = /* glsl */ `#version 300 es
precision highp float; in vec2 vUv; uniform sampler2D uTex; uniform vec2 uTexel; out vec4 o;
float lum(vec3 c){ return dot(c, vec3(0.299,0.587,0.114)); }
void main(){
  vec3 rgbNW = texture(uTex, vUv + vec2(-1,-1)*uTexel).rgb;
  vec3 rgbNE = texture(uTex, vUv + vec2(1,-1)*uTexel).rgb;
  vec3 rgbSW = texture(uTex, vUv + vec2(-1,1)*uTexel).rgb;
  vec3 rgbSE = texture(uTex, vUv + vec2(1,1)*uTexel).rgb;
  vec3 rgbM = texture(uTex, vUv).rgb;
  float lNW=lum(rgbNW), lNE=lum(rgbNE), lSW=lum(rgbSW), lSE=lum(rgbSE), lM=lum(rgbM);
  float lMin = min(lM, min(min(lNW,lNE),min(lSW,lSE)));
  float lMax = max(lM, max(max(lNW,lNE),max(lSW,lSE)));
  vec2 dir = vec2(-((lNW+lNE)-(lSW+lSE)), ((lNW+lSW)-(lNE+lSE)));
  float red = max((lNW+lNE+lSW+lSE)*0.03125, 1.0/128.0);
  float rcp = 1.0/(min(abs(dir.x),abs(dir.y))+red);
  dir = clamp(dir*rcp, -8.0, 8.0)*uTexel;
  vec3 a = 0.5*(texture(uTex, vUv + dir*(1.0/3.0-0.5)).rgb + texture(uTex, vUv + dir*(2.0/3.0-0.5)).rgb);
  vec3 b = a*0.5 + 0.25*(texture(uTex, vUv - dir*0.5).rgb + texture(uTex, vUv + dir*0.5).rgb);
  float lb = lum(b);
  o = vec4((lb<lMin||lb>lMax)? a : b, 1.0);
}`;
export const copyFS = /* glsl */ `#version 300 es
precision highp float; in vec2 vUv; uniform sampler2D uTex; out vec4 o; void main(){ o = texture(uTex, vUv); }`;
